import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { useAppDispatch } from '@app/hooks';
import {
  useGetAdvisorUsageQuery,
  useListAdvisorMessagesQuery,
  useListAdvisorSessionsQuery,
  useMeQuery,
} from '@shared/api';
import {
  askAdvisorStream,
  reloadAdvisorChat,
  storeAdvisorAnswer,
  type AdvisorStreamError,
} from '@shared/api/advisorStream';
import {
  apiErrorMessage,
  fmtUsd,
  useProgressPermission,
  type AdvisorAnswer,
  type AdvisorAttachmentMeta,
} from '@entities/progress';
import { Brand } from '@shared/ui/Brand';
import {
  IconArrowDown,
  IconArrowLeft,
  IconPanelLeft,
  IconPlus,
  IconSearch,
  IconSparkles,
  IconX,
} from '@shared/ui/icons';
import { MaintenanceBanner } from '@widgets/Maintenance/MaintenanceBanner';
import { ThemeToggle } from '@widgets/AppShell/ThemeToggle';
import { Composer } from './Composer';
import { LimitsModal } from './LimitsModal';
import { SessionList } from './SessionList';
import { Thread, type PendingQuestion } from './Thread';
import { UsageMeter } from './UsageMeter';

/* ОСНОВА.Эксперт — чат руководителя отдельной страницей во весь экран, в духе
   ChatGPT: слева — свои чаты, поиск по ним и лимит месяца, справа — диалог и
   крупное поле вопроса. Меню ОСНОВЫ здесь нет, зато в шапке всегда есть
   «Вернуться в ОСНОВУ» — туда, откуда пришли. Адрес чата — /expert/:sessionId;
   без id — новый чат (заводится после первого ответа). Доступ — как к
   Руководителю (чтение агента progress).

   Ответ приходит потоком и печатается по мере генерации. Когда он допечатан,
   реплики кладутся в кэш чата, новый чат переходит на свой адрес, и только
   когда ответ уже стоит в ленте чата, черновая реплика убирается — без скачков. */

const BASE = '/expert';
/** Куда возвращаться, если не знаем, откуда пришли. */
const HOME = '/agents/progress';
/** sessionStorage: откуда пришли в Эксперта (переживает переходы между чатами). */
const RETURN_KEY = 'osnova.expert.return';
/** localStorage: развёрнут ли список чатов на компьютере. */
const SIDEBAR_KEY = 'osnova.expert.sidebar';
const SIDE_W = '280px';
/** Уже этого — список чатов выезжает поверх диалога. */
const MOBILE = '(max-width: 860px)';

/* ── Каркас ──────────────────────────────────────────────────────────────── */

const Page = styled.div<{ $side: boolean }>`
  display: grid;
  grid-template-columns: ${({ $side }) => ($side ? SIDE_W : '0px')} minmax(0, 1fr);
  /* Первый ряд — плашка об обновлении (MaintenanceBanner), без работ пустая. */
  grid-template-rows: auto minmax(0, 1fr);
  grid-template-areas:
    'banner banner'
    'side main';
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  transition: grid-template-columns ${({ theme }) => theme.motion.base}
    ${({ theme }) => theme.motion.easeOut};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
  @media ${MOBILE} {
    grid-template-columns: 0px minmax(0, 1fr);
  }
`;

const Side = styled.aside<{ $open: boolean; $drawer: boolean }>`
  grid-area: side;
  min-height: 0;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border-right: 1px solid ${({ theme }) => theme.colors.border};
  /* Свёрнутый список убираем и из Tab-обхода: прячем после анимации. */
  visibility: ${({ $open }) => ($open ? 'visible' : 'hidden')};
  transition: visibility 0s linear ${({ $open, theme }) => ($open ? '0s' : theme.motion.base)};

  @media ${MOBILE} {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: ${({ theme }) => theme.z.overlay + 1};
    width: min(300px, 86vw);
    visibility: ${({ $drawer }) => ($drawer ? 'visible' : 'hidden')};
    transform: translateX(${({ $drawer }) => ($drawer ? '0' : '-100%')});
    box-shadow: ${({ $drawer, theme }) => ($drawer ? theme.shadow.lg : 'none')};
    transition:
      transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut},
      visibility 0s linear ${({ $drawer, theme }) => ($drawer ? '0s' : theme.motion.base)};
  }
`;

/** Содержимое списка фиксированной ширины: при сворачивании колонка едет к
 *  нулю, а строки не переносятся на глазах. */
const SideInner = styled.div`
  display: flex;
  flex-direction: column;
  width: ${SIDE_W};
  height: 100%;

  @media ${MOBILE} {
    width: 100%;
  }
`;

const Backdrop = styled.div<{ $show: boolean }>`
  display: none;

  @media ${MOBILE} {
    display: block;
    position: fixed;
    inset: 0;
    z-index: ${({ theme }) => theme.z.overlay};
    background: rgba(0, 0, 0, 0.45);
    opacity: ${({ $show }) => ($show ? 1 : 0)};
    pointer-events: ${({ $show }) => ($show ? 'auto' : 'none')};
    transition: opacity ${({ theme }) => theme.motion.base};
  }
`;

const IconBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  padding: 0;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  cursor: pointer;
  transition:
    background ${({ theme }) => theme.motion.fast},
    color ${({ theme }) => theme.motion.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

/* ── Список чатов ────────────────────────────────────────────────────────── */

const SideTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 60px;
  padding: ${({ theme }) => `0 ${theme.spacing(2)} 0 ${theme.spacing(4)}`};
`;

const BrandLink = styled(Link)`
  display: inline-flex;
  padding: ${({ theme }) => `${theme.spacing(1)} 0`};
  border-radius: ${({ theme }) => theme.radius.sm};
  text-decoration: none;

  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const SideNav = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  padding: ${({ theme }) => `0 ${theme.spacing(3)} ${theme.spacing(3)}`};
`;

const NewChat = styled(Link)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14.5px;
  font-weight: 600;
  text-decoration: none;
  transition: background ${({ theme }) => theme.motion.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const NewChatIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: ${({ theme }) => theme.radius.pill};
  color: #fff;
  background: ${({ theme }) => theme.colors.gradient.brand};
`;

const Search = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `0 ${theme.spacing(2)} 0 ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.muted};
  transition: border-color ${({ theme }) => theme.motion.fast};

  &:focus-within {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
  input {
    flex: 1;
    min-width: 0;
    height: 38px;
    border: 0;
    outline: none;
    background: transparent;
    color: ${({ theme }) => theme.colors.text.primary};
    font: inherit;
    font-size: 14px;
  }
  input:focus-visible {
    box-shadow: none;
  }
  input::placeholder {
    color: ${({ theme }) => theme.colors.text.muted};
  }
  button {
    display: inline-flex;
    border: 0;
    padding: 4px;
    border-radius: ${({ theme }) => theme.radius.xs};
    background: transparent;
    color: inherit;
    cursor: pointer;
  }
  button:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const SideScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)} ${theme.spacing(4)}`};
`;

const SideFoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(4)};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const TextBtn = styled.button`
  align-self: flex-start;
  border: 0;
  padding: 0;
  background: none;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const Me = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  min-width: 0;
`;

const MeAvatar = styled.div`
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: ${({ theme }) => theme.colors.gradient.brand};
  font-size: 13px;
  font-weight: 600;
`;

const MeText = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-size: 13px;
  line-height: 1.3;

  strong,
  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  span {
    color: ${({ theme }) => theme.colors.text.muted};
    font-size: 12px;
  }
`;

/* ── Диалог ──────────────────────────────────────────────────────────────── */

const Main = styled.main`
  grid-area: main;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
`;

const Top = styled.header`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 60px;
  flex-shrink: 0;
  padding: ${({ theme }) => `0 ${theme.spacing(4)}`};

  @media (max-width: 560px) {
    padding: ${({ theme }) => `0 ${theme.spacing(2)}`};
  }
`;

/** Кнопки шапки, нужные, только когда список чатов спрятан. */
const WhenListHidden = styled.span<{ $listShown: boolean }>`
  display: inline-flex;
  gap: ${({ theme }) => theme.spacing(1)};

  ${({ $listShown }) =>
    $listShown &&
    css`
      @media not all and ${MOBILE} {
        display: none;
      }
    `}
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2.5)};
  min-width: 0;
  padding-left: ${({ theme }) => theme.spacing(1)};
`;

const TitleMark = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: ${({ theme }) => theme.radius.md};
  color: #fff;
  background: ${({ theme }) => theme.colors.gradient.brand};
  box-shadow: ${({ theme }) => theme.shadow.sm};

  @media (max-width: 480px) {
    display: none;
  }
`;

const TitleText = styled.span`
  min-width: 0;
  font-size: 17px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 400px) {
    font-size: 15px;
  }
`;

const ModelTag = styled.span`
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.surfaceHover};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;

  @media (max-width: 720px) {
    display: none;
  }
`;

const ChatName = styled.div`
  flex: 1;
  min-width: 0;
  padding: ${({ theme }) => `0 ${theme.spacing(4)}`};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 1100px) {
    display: none;
  }
`;

const TopRight = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-left: auto;
`;

const ThemeSlot = styled.span`
  display: inline-flex;

  @media (max-width: 480px) {
    display: none;
  }
`;

const BackBtn = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 38px;
  padding: ${({ theme }) => `0 ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.colors.borderStrong};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition:
    border-color ${({ theme }) => theme.motion.fast},
    color ${({ theme }) => theme.motion.fast};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }

  .short {
    display: none;
  }
  @media (max-width: 640px) {
    padding: ${({ theme }) => `0 ${theme.spacing(3)}`};
    .long {
      display: none;
    }
    .short {
      display: inline;
    }
  }
`;

const Scroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${({ theme }) => `${theme.spacing(6)} ${theme.spacing(5)} ${theme.spacing(10)}`};

  @media (max-width: 560px) {
    padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(4)} ${theme.spacing(8)}`};
  }
`;

const Bottom = styled.div`
  position: relative;
  flex-shrink: 0;
  padding: ${({ theme }) => `0 ${theme.spacing(5)} ${theme.spacing(3)}`};

  & > * {
    max-width: 780px;
    margin-left: auto;
    margin-right: auto;
  }
  /* Лента уходит под поле плавно, а не обрезается по линейке. */
  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: -28px;
    height: 28px;
    background: linear-gradient(to bottom, transparent, ${({ theme }) => theme.colors.background});
    pointer-events: none;
  }

  @media (max-width: 560px) {
    padding: ${({ theme }) => `0 ${theme.spacing(3)} ${theme.spacing(2)}`};
  }
`;

const ToBottom = styled.button`
  position: absolute;
  top: -56px;
  left: 50%;
  z-index: 1;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.secondary};
  box-shadow: ${({ theme }) => theme.shadow.md};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const ErrorLine = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2.5)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.colors.status.danger};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.status.danger};
  font-size: 14px;
  text-align: left;
`;

/* ── Новый чат ───────────────────────────────────────────────────────────── */

const WelcomeArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => `${theme.spacing(6)} ${theme.spacing(5)}`};

  @media (max-width: 560px) {
    padding: ${({ theme }) => `${theme.spacing(4)} ${theme.spacing(3)}`};
  }
`;

const WelcomeInner = styled.div`
  width: 100%;
  max-width: 780px;
  /* По центру экрана, а при нехватке высоты — прокрутка сверху. */
  margin: auto;
  text-align: center;
`;

const Emblem = styled.div`
  width: 60px;
  height: 60px;
  margin: 0 auto ${({ theme }) => theme.spacing(5)};
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: ${({ theme }) => theme.colors.gradient.brand};
  box-shadow: ${({ theme }) => theme.shadow.primary};
`;

const Hello = styled.h1`
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
  font-size: 32px;
  font-weight: 700;
  letter-spacing: -0.01em;

  @media (max-width: 560px) {
    font-size: 26px;
  }
`;

const HelloText = styled.p`
  margin: 0 auto ${({ theme }) => theme.spacing(8)};
  max-width: 600px;
  font-size: 16px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Suggestions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  margin-top: ${({ theme }) => theme.spacing(6)};
  text-align: left;

  @media (max-width: 560px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Suggestion = styled.button`
  padding: ${({ theme }) => `${theme.spacing(3.5)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition:
    border-color ${({ theme }) => theme.motion.fast},
    background ${({ theme }) => theme.motion.fast};

  strong {
    display: block;
    margin-bottom: ${({ theme }) => theme.spacing(1)};
    font-size: 14.5px;
  }
  span {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    font-size: 13.5px;
    line-height: 1.45;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    background: ${({ theme }) => theme.colors.surface};
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

const Note = styled.p`
  margin: ${({ theme }) => theme.spacing(6)} auto 0;
  max-width: 640px;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Center = styled.div`
  padding: ${({ theme }) => theme.spacing(10)};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  font-size: 15px;
`;

const Gate = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(5)};
  min-height: 100vh;
  padding: ${({ theme }) => theme.spacing(6)};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
  text-align: center;
`;

/* ── Данные ──────────────────────────────────────────────────────────────── */

type SuggestionItem = { title: string; text: string };

/** Подсказки, которым нужны данные ОСНОВЫ. */
const ATTENTION_SUGGESTION: SuggestionItem = {
  title: 'Что требует внимания',
  text: 'Пройдись по нашим объектам: где Заказчик ждёт ответа на письма, какие претензии открыты и какие сроки горят в ближайшие две недели? Дай ссылки.',
};
const CONTRACTS_SUGGESTION: SuggestionItem = {
  title: 'Наши договоры',
  text: 'Найди в наших договорах условия о неустойке за просрочку подрядчика и сравни их: где риски для нас выше? Со ссылками на договоры.',
};

/** Подсказка, которая без файлов не сработает. */
const FILE_SUGGESTION: SuggestionItem = {
  title: 'Документ в Word',
  text: 'Подготовь в Word служебную записку генеральному директору о росте цен на металл и его влиянии на маржу объекта, с таблицей и вариантами решений.',
};

/** Подсказка, которая без поиска не сработает. */
const COMPANY_SUGGESTION: SuggestionItem = {
  title: 'Справка о компании',
  text: 'Найди в интернете сведения об ООО «Запсибнефтехим»: руководство, свежие новости, крупные стройки и закупки — со ссылками на источники.',
};

const SUGGESTIONS: ReadonlyArray<SuggestionItem> = [
  {
    title: 'Письмо Заказчику',
    text: 'Составь письмо Заказчику о переносе срока работ из-за задержки проектной документации: вежливо, со ссылкой на договор и с предложением нового графика.',
  },
  {
    title: 'Разбор договора',
    text: 'Приложу договор подряда — найди риски для подрядчика: штрафы, сроки приёмки, гарантийные удержания, односторонний отказ.',
  },
  {
    title: 'Расчёт',
    text: 'Посчитай, сколько бригад нужно, чтобы забить 1 200 свай за 45 рабочих дней при выработке 14 свай на бригаду в смену. Покажи расчёт.',
  },
  {
    title: 'Служебная записка',
    text: 'Подготовь служебную записку генеральному директору о росте цен на металл и его влиянии на маржу объекта, с вариантами решений.',
  },
];

/** Вопрос, на который Эксперт сейчас отвечает. */
type Pending = PendingQuestion & {
  /** Чат вопроса (`null` — новый); когда ответ записан — чат ответа. */
  session: string | null;
  /** Итог с сервера (после `done`). */
  result: AdvisorAnswer | null;
  /** Ответ допечатан на экране. */
  revealed: boolean;
  /** Ответ положен в кэш чата. */
  stored: boolean;
};

function attachmentsMeta(files: File[]): AdvisorAttachmentMeta[] {
  return files.map((f) => ({
    name: f.name,
    kind: f.type.startsWith('image/') ? 'image' : 'document',
    chars: 1,
  }));
}

function errorText(err: unknown): string {
  const msg = apiErrorMessage(err);
  if (msg) return msg;
  const status = (err as { status?: unknown } | null)?.status;
  if (status === 'FETCH_ERROR') return 'Нет связи с сервером — проверьте интернет и повторите.';
  if (status === 413) return 'Файлы слишком большие — уменьшите их или приложите по одному.';
  return 'Эксперт не ответил — попробуйте ещё раз.';
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/** Свой адрес ОСНОВЫ, не сам Эксперт и не чужой сайт. */
function isReturnPath(p: string): boolean {
  return p.startsWith('/') && !p.startsWith('//') && p !== BASE && !p.startsWith(`${BASE}/`);
}

/**
 * Куда ведёт «Вернуться в ОСНОВУ»: страница, с которой пришли (ссылки на
 * Эксперта передают её в `state.from`). Запоминаем на вкладку браузера — между
 * чатами state теряется, а после перезагрузки страницы его нет вовсе. Не знаем
 * откуда — к Руководителю.
 */
function useReturnPath(): string {
  const location = useLocation();
  const [path] = useState(() => {
    const from = (location.state as { from?: unknown } | null)?.from;
    if (typeof from === 'string' && isReturnPath(from)) {
      try {
        sessionStorage.setItem(RETURN_KEY, from);
      } catch {
        // Хранилище недоступно — вернёмся по from хотя бы в этот раз.
      }
      return from;
    }
    try {
      const saved = sessionStorage.getItem(RETURN_KEY);
      if (saved && isReturnPath(saved)) return saved;
    } catch {
      // Недоступно — к Руководителю.
    }
    return HOME;
  });
  return path;
}

function isMobile(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(MOBILE).matches;
}

export function ExpertPage() {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const returnTo = useReturnPath();
  const { data: me, isLoading: meLoading } = useMeQuery();
  const { can_read } = useProgressPermission();
  const isAdmin = (me?.roles ?? []).includes('admin');

  const { data: sessions } = useListAdvisorSessionsQuery(undefined, { skip: !can_read });
  const { data: usage } = useGetAdvisorUsageQuery(undefined, { skip: !can_read });
  const {
    data: messages,
    isFetching: messagesLoading,
    isError: messagesError,
  } = useListAdvisorMessagesQuery(sessionId ?? '', { skip: !can_read || !sessionId });
  const dispatch = useAppDispatch();

  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [query, setQuery] = useState('');
  const [focusSignal, setFocusSignal] = useState(0);
  const [limitsOpen, setLimitsOpen] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  // Список чатов: на компьютере — колонка (сворачивается, запоминаем), на
  // телефоне — выезжающая панель (по умолчанию закрыта).
  const [sideOpen, setSideOpen] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) !== '0';
    } catch {
      return true;
    }
  });
  const [drawer, setDrawer] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const seen = useRef<{ session: string | undefined; count: number }>({
    session: undefined,
    count: 0,
  });
  // Лента у нижнего края — печатающийся ответ тянет её за собой; отмотал вверх
  // читать — не дёргаем.
  const stick = useRef(true);
  // Текущий запрос: уходим со страницы — отменяем (сервер ответ всё равно
  // допишет и сохранит в чате).
  const request = useRef<AbortController | null>(null);

  const current = useMemo(
    () => sessions?.find((s) => s.id === sessionId),
    [sessions, sessionId],
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('ru-RU');
    if (!q || !sessions) return sessions;
    return sessions.filter((s) => s.title.toLocaleLowerCase('ru-RU').includes(q));
  }, [sessions, query]);
  // Ответ уже стоит в ленте чата — черновая реплика больше не нужна.
  const answerInThread =
    !!pending?.result && !!messages?.some((m) => m.id === pending.result?.answer.id);
  const shownPending =
    pending && pending.session === (sessionId ?? null) && !answerInThread ? pending : null;
  const pendingVisible = shownPending !== null;
  const busy = pending !== null;

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, sideOpen ? '1' : '0');
    } catch {
      // Запись недоступна — состояние не переживёт перезагрузку.
    }
  }, [sideOpen]);

  // Смена чата: закрыть панель на телефоне, сбросить ошибку.
  useEffect(() => {
    setDrawer(false);
    setError(null);
    setAtBottom(true);
  }, [sessionId]);

  // Esc закрывает выехавший список чатов.
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawer(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawer]);

  // Заголовок вкладки браузера — как у отдельного приложения.
  useEffect(() => {
    const prev = document.title;
    document.title = current?.title ? `${current.title} — ОСНОВА.Эксперт` : 'ОСНОВА.Эксперт';
    return () => {
      document.title = prev;
    };
  }, [current?.title]);

  // Удалённый или чужой чат — на новый.
  useEffect(() => {
    if (sessionId && messagesError) navigate(BASE, { replace: true });
  }, [sessionId, messagesError, navigate]);

  // К последнему сообщению: открытый чат — сразу, новая реплика — плавно (если
  // человек не отмотал ленту вверх читать).
  const count = (messages?.length ?? 0) + (shownPending ? 1 : 0);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const sameChat = seen.current.session === sessionId && seen.current.count > 0;
    seen.current = { session: sessionId, count };
    if (sameChat && !stick.current) return;
    el.scrollTo({ top: el.scrollHeight, behavior: sameChat ? 'smooth' : 'auto' });
  }, [count, sessionId]);

  // Уходим со страницы — отменить запрос (ответ сервер всё равно сохранит).
  useEffect(() => () => request.current?.abort(), []);

  // Ответ допечатан (или его не видно — человек в другом чате) — в кэш чата;
  // новый чат переходит на свой адрес. Чат черновой реплики не меняем: она
  // пропадёт сама, когда сменится адрес, — без кадра с приветствием посередине.
  useEffect(() => {
    if (!pending?.result || pending.stored) return;
    if (pendingVisible && !pending.revealed) return;
    const res = pending.result;
    storeAdvisorAnswer(dispatch, pending.session, res);
    setPending({ ...pending, stored: true });
    if (!pending.session && pendingVisible) navigate(`${BASE}/${res.session.id}`, { replace: true });
  }, [pending, pendingVisible, dispatch, navigate]);

  // Ответ встал в ленту чата (или чат уже другой) — черновая реплика не нужна.
  useEffect(() => {
    if (pending?.stored && !pendingVisible) setPending(null);
  }, [pending?.stored, pendingVisible]);

  // Страховка: ответ записан, а в ленте его всё нет (кэш чата не обновился) —
  // перечитать чат с сервера и не держать поле вопроса закрытым.
  const storedSession = pending?.stored ? pending.session : undefined;
  useEffect(() => {
    if (storedSession === undefined || !pendingVisible) return;
    const t = window.setTimeout(() => {
      if (storedSession) reloadAdvisorChat(dispatch, storedSession);
      setPending(null);
    }, 2500);
    return () => window.clearTimeout(t);
  }, [storedSession, pendingVisible, dispatch]);

  const onRevealed = useCallback(() => {
    setPending((p) => (p && p.done && !p.revealed ? { ...p, revealed: true } : p));
  }, []);

  // Печатающийся ответ подрос — лента следом, если она у нижнего края.
  const onGrow = useCallback(() => {
    const el = scroller.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, []);

  function onScroll() {
    const el = scroller.current;
    if (!el) return;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    stick.current = bottom;
    setAtBottom(bottom);
  }

  function toggleList() {
    if (isMobile()) setDrawer((v) => !v);
    else setSideOpen((v) => !v);
  }

  async function submit() {
    if (busy) return;
    const question = text.trim();
    if (!question && files.length === 0) return;
    const asked = { session: sessionId ?? null, draft: text, files };
    setError(null);
    stick.current = true;
    setPending({
      session: asked.session,
      text: question,
      files: attachmentsMeta(files),
      startedAt: Date.now(),
      answer: '',
      searching: false,
      coding: false,
      tool: null,
      done: false,
      result: null,
      revealed: false,
      stored: false,
    });
    // Вопрос уже виден в ленте — поле очищаем сразу, как в ChatGPT.
    setText('');
    setFiles([]);
    const ctrl = new AbortController();
    request.current = ctrl;
    try {
      const res = await askAdvisorStream(
        { session_id: asked.session, question, files: asked.files },
        {
          onDelta: (t) =>
            setPending((p) =>
              p
                ? { ...p, answer: p.answer + t, searching: false, coding: false, tool: null }
                : p,
            ),
          onSearching: () => setPending((p) => (p ? { ...p, searching: true } : p)),
          onCoding: () => setPending((p) => (p ? { ...p, coding: true, tool: null } : p)),
          onTool: (label) =>
            setPending((p) => (p ? { ...p, tool: label, searching: false, coding: false } : p)),
          onReset: () => setPending((p) => (p ? { ...p, answer: '' } : p)),
        },
        ctrl.signal,
      );
      // Итог — с источниками: экран допечатывает его, дальше — эффекты выше.
      setPending((p) =>
        p
          ? {
              ...p,
              answer: res.answer.content,
              searching: false,
              coding: false,
              tool: null,
              done: true,
              result: res,
            }
          : p,
      );
    } catch (err) {
      if ((err as AdvisorStreamError | null)?.status === 'ABORTED') return;
      setError(errorText(err));
      // Ответа нет — возвращаем черновик и файлы, если человек не начал новый.
      setText((cur) => cur || asked.draft);
      setFiles((cur) => (cur.length ? cur : asked.files));
      setPending(null);
    } finally {
      if (request.current === ctrl) request.current = null;
    }
  }

  if (meLoading) return <Gate aria-busy>Загрузка…</Gate>;
  if (!can_read) {
    return (
      <Gate>
        Эксперт доступен руководителям с доступом к ОСНОВА.Руководитель.
        <BackBtn to="/">
          <IconArrowLeft size={16} /> Вернуться в ОСНОВУ
        </BackBtn>
      </Gate>
    );
  }

  const exhausted = !!usage?.exhausted;
  const showWelcome = !sessionId && !shownPending;
  const webSearch = !!usage?.web_search;
  const makesFiles = !!usage?.files;
  const knowsOsnova = !!usage?.osnova_data;
  // С поиском — подсказка о компании вместо служебной записки.
  // С файлами — документ в Word вместо служебной записки; с поиском — справка о компании.
  // С данными ОСНОВЫ — сводка по объектам и поиск по договорам вместо общих примеров.
  const suggestions = [
    knowsOsnova ? ATTENTION_SUGGESTION : SUGGESTIONS[0],
    knowsOsnova ? CONTRACTS_SUGGESTION : SUGGESTIONS[1],
    webSearch ? COMPANY_SUGGESTION : SUGGESTIONS[2],
    makesFiles ? FILE_SUGGESTION : SUGGESTIONS[3],
  ];

  const composer = (
    <Composer
      text={text}
      files={files}
      busy={busy}
      disabled={exhausted}
      disabledHint={
        exhausted
          ? 'Лимит на этот месяц исчерпан — новый с 1-го числа или у администратора'
          : undefined
      }
      webSearch={webSearch}
      focusSignal={focusSignal}
      onText={setText}
      onFiles={setFiles}
      onError={setError}
      onSubmit={() => void submit()}
    />
  );

  return (
    <Page $side={sideOpen}>
      <MaintenanceBanner />

      <Side $open={sideOpen} $drawer={drawer} aria-label="Чаты Эксперта">
        <SideInner>
          <SideTop>
            <BrandLink to={returnTo} title="Вернуться в ОСНОВУ">
              <Brand $size="sm" $animate={false} />
            </BrandLink>
            <IconBtn
              type="button"
              onClick={toggleList}
              aria-label="Скрыть список чатов"
              title="Скрыть список чатов"
            >
              <IconPanelLeft size={18} />
            </IconBtn>
          </SideTop>
          <SideNav>
            <NewChat to={BASE} onClick={() => setDrawer(false)}>
              <NewChatIcon aria-hidden>
                <IconPlus size={15} />
              </NewChatIcon>
              Новый чат
            </NewChat>
            <Search>
              <IconSearch size={16} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setQuery('');
                }}
                placeholder="Поиск по чатам"
                aria-label="Поиск по чатам"
              />
              {query && (
                <button type="button" onClick={() => setQuery('')} aria-label="Очистить поиск">
                  <IconX size={14} />
                </button>
              )}
            </Search>
          </SideNav>
          <SideScroll>
            <SessionList
              sessions={filtered}
              activeId={sessionId}
              emptyText={query.trim() ? 'Ничего не нашлось.' : undefined}
              onDeleted={(id) => {
                if (id === sessionId) navigate(BASE, { replace: true });
              }}
            />
          </SideScroll>
          <SideFoot>
            <UsageMeter usage={usage} />
            {isAdmin && (
              <TextBtn type="button" onClick={() => setLimitsOpen(true)}>
                Лимиты руководителей
              </TextBtn>
            )}
            {me?.user && (
              <Me>
                <MeAvatar aria-hidden>{initials(me.user.full_name)}</MeAvatar>
                <MeText>
                  <strong>{me.user.full_name}</strong>
                  <span>{me.user.department ?? 'без отдела'}</span>
                </MeText>
              </Me>
            )}
          </SideFoot>
        </SideInner>
      </Side>
      <Backdrop $show={drawer} onClick={() => setDrawer(false)} aria-hidden />

      <Main>
        <Top>
          <WhenListHidden $listShown={sideOpen}>
            <IconBtn
              type="button"
              onClick={toggleList}
              aria-label="Показать список чатов"
              title="Показать список чатов"
            >
              <IconPanelLeft size={18} />
            </IconBtn>
            <IconBtn as={Link} to={BASE} aria-label="Новый чат" title="Новый чат">
              <IconPlus size={18} />
            </IconBtn>
          </WhenListHidden>
          <Title>
            <TitleMark aria-hidden>
              <IconSparkles size={16} />
            </TitleMark>
            <TitleText>ОСНОВА.Эксперт</TitleText>
            {usage && (
              <ModelTag title="Модель, которая отвечает">
                {usage.model}
                {usage.web_search && ' · поиск в интернете'}
              </ModelTag>
            )}
          </Title>
          <ChatName title={current?.title}>{sessionId ? (current?.title ?? '') : ''}</ChatName>
          <TopRight>
            <ThemeSlot>
              <ThemeToggle />
            </ThemeSlot>
            <BackBtn to={returnTo} title="Вернуться в ОСНОВУ">
              <IconArrowLeft size={16} />
              <span className="long">Вернуться в ОСНОВУ</span>
              <span className="short">ОСНОВА</span>
            </BackBtn>
          </TopRight>
        </Top>

        {showWelcome ? (
          <WelcomeArea>
            <WelcomeInner>
              <Emblem aria-hidden>
                <IconSparkles size={30} />
              </Emblem>
              <Hello>Чем могу помочь?</Hello>
              <HelloText>
                {knowsOsnova && 'Знаю данные ОСНОВЫ: объекты, договоры, переписку, претензии и совещания. '}
                Письма и служебные записки, разбор договоров и претензий, расчёты, справки,
                подготовка к совещаниям{makesFiles ? ', документы в Word, PDF, Excel и PowerPoint' : ''}.
                Приложите документы или фото — Эксперт прочитает их и ответит по существу.
              </HelloText>
              {error && <ErrorLine role="alert">{error}</ErrorLine>}
              {composer}
              <Suggestions>
                {suggestions.map((s) => (
                  <Suggestion
                    key={s.title}
                    type="button"
                    onClick={() => {
                      setText(s.text);
                      setFocusSignal((n) => n + 1);
                    }}
                  >
                    <strong>{s.title}</strong>
                    <span>{s.text}</span>
                  </Suggestion>
                ))}
              </Suggestions>
              <Note>
                Отвечает только по рабочим вопросам.{' '}
                {makesFiles && 'Готовые файлы появляются под ответом — их можно скачать. '}
                {knowsOsnova
                  ? 'Отвечает по данным ОСНОВЫ и ставит ссылки на договоры, письма и объекты. '
                  : 'Данных ОСНОВЫ у Эксперта нет — нужное прикладывайте файлом. '}
                {webSearch
                  ? 'Свежие сведения о компаниях, ценах и нормативах ищет в интернете и даёт ссылки на источники.'
                  : 'Интернета у Эксперта нет — свежие сведения проверяйте отдельно.'}
                {usage && ` Ваш лимит — ${fmtUsd(usage.limit_usd)} в месяц.`}
              </Note>
            </WelcomeInner>
          </WelcomeArea>
        ) : (
          <>
            <Scroll ref={scroller} onScroll={onScroll}>
              {messagesLoading && !messages ? (
                <Center>Загрузка чата…</Center>
              ) : (
                <Thread
                  messages={sessionId ? (messages ?? []) : []}
                  pending={shownPending}
                  onRevealed={onRevealed}
                  onGrow={onGrow}
                />
              )}
            </Scroll>
            <Bottom>
              {!atBottom && (
                <ToBottom
                  type="button"
                  onClick={() =>
                    scroller.current?.scrollTo({
                      top: scroller.current.scrollHeight,
                      behavior: 'smooth',
                    })
                  }
                  aria-label="К последнему сообщению"
                  title="К последнему сообщению"
                >
                  <IconArrowDown size={18} />
                </ToBottom>
              )}
              {error && <ErrorLine role="alert">{error}</ErrorLine>}
              {composer}
            </Bottom>
          </>
        )}
      </Main>

      {isAdmin && <LimitsModal open={limitsOpen} onClose={() => setLimitsOpen(false)} />}
    </Page>
  );
}
